import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../config/api';
import moment from 'moment';
import PageNavbar from './PageNavbar';
import { formatDateDisplay } from '../utils/formatDate';
import './StoreManagerDashboard.css';

// Format DB category value for display (e.g. "Store-1" -> "Store 1", "Online Orders" -> "Online order")
const formatCategoryLabel = (category) => {
  if (!category) return '';
  if (category === 'Online Orders') return 'Online order';
  if (category === 'Store-1') return 'Store 1';
  if (category === 'Store-2') return 'Store 2';
  return category;
};

const StoreManagerDashboard = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [categories, setCategories] = useState([]);
  const [serverDate, setServerDate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [listPeriod, setListPeriod] = useState(null);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await api.get('/orders/categories');
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
      setCategories([]);
    }
  }, []);

  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      const dateRes = await api.get('/orders/server-date');
      const serverToday = dateRes.data?.serverDate;
      if (serverToday) setServerDate(serverToday);
      const startDate = serverToday || moment().format('YYYY-MM-DD');
      const endDate = moment(startDate).add(30, 'days').format('YYYY-MM-DD');
      const ordersRes = await api.get('/orders', {
        params: { startDate, endDate }
      });
      const ordersData = ordersRes.data?.orders ?? [];
      setOrders(Array.isArray(ordersData) ? ordersData : []);
      if (ordersRes.data.serverDate) setServerDate(ordersRes.data.serverDate);
    } catch (error) {
      console.error('Error fetching orders:', error);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  // Extract YYYY-MM-DD from delivery_date (column used for Today / Tomorrow / Future)
  const getDatePart = (d) => {
    if (!d) return '';
    const s = typeof d === 'string' ? d : moment(d).format('YYYY-MM-DD');
    return s.split('T')[0];
  };

  const todayStr = serverDate || moment().format('YYYY-MM-DD');
  const tomorrowStr = moment(todayStr).add(1, 'days').format('YYYY-MM-DD');

  const { todayOrders, tomorrowOrders, futureOrders, counts } = useMemo(() => {
    const today = orders.filter(o => getDatePart(o.delivery_date) === todayStr);
    const tomorrow = orders.filter(o => getDatePart(o.delivery_date) === tomorrowStr);
    const future = orders.filter(o => getDatePart(o.delivery_date) > tomorrowStr);
    const c = {};
    categories.forEach(cat => {
      c[cat] = {
        today: today.filter(o => o.category === cat).length,
        tomorrow: tomorrow.filter(o => o.category === cat).length,
        future: future.filter(o => o.category === cat).length
      };
    });
    return { todayOrders: today, tomorrowOrders: tomorrow, futureOrders: future, counts: c };
  }, [orders, categories, todayStr, tomorrowStr]);

  const listOrders = listPeriod === 'today' ? todayOrders : listPeriod === 'tomorrow' ? tomorrowOrders : listPeriod === 'future' ? futureOrders : [];
  const listLabel =
    listPeriod === 'today'
      ? `Today (${formatDateDisplay(todayStr)})`
      : listPeriod === 'tomorrow'
        ? `Tomorrow (${formatDateDisplay(tomorrowStr)})`
        : listPeriod === 'future'
          ? 'Future'
          : '';

  const getProductDescription = (order) => {
    const items = order.items || [];
    if (items.length === 0) return '-';
    const descs = items.map(item => {
      if (item.item_type === 'Cakes' && item.product_name) {
        return item.flavour ? `${item.product_name} (${item.flavour})` : item.product_name;
      }
      if (item.item_type === 'Desserts' && item.dessert_name) return item.dessert_name;
      if (item.item_type === 'Cup Cakes' && item.cup_cake_name) return item.cup_cake_name;
      if (item.item_type === 'Plants' && item.plant_name) return item.plant_name;
      if (item.item_type === 'Snack Boxes' && item.snack_box_items?.length) {
        const names = item.snack_box_items.map(s => `${s.item_name} x${s.quantity}`).join(', ');
        return `Snack Box: ${names}`;
      }
      return item.item_type;
    });
    return descs.join('; ');
  };

  return (
    <div className="store-manager-dashboard">
      <PageNavbar />
      <div className="container">
        <header className="sm-page-header">
          <h1>Store Manager</h1>
          <p className="sm-page-lead">Today, tomorrow, and upcoming fulfilment at a glance.</p>
        </header>

        {loading ? (
          <div className="loading">Loading...</div>
        ) : (
          <>
            <div className="sm-period-cards" role="group" aria-label="Fulfilment periods">
              {[
                { key: 'today', label: 'Today', date: formatDateDisplay(todayStr), count: todayOrders.length },
                { key: 'tomorrow', label: 'Tomorrow', date: formatDateDisplay(tomorrowStr), count: tomorrowOrders.length },
                { key: 'future', label: 'Future', date: 'Next 30 days', count: futureOrders.length }
              ].map((p) => (
                <button
                  key={p.key}
                  type="button"
                  className={`sm-period-card${listPeriod === p.key ? ' is-active' : ''}`}
                  onClick={() => setListPeriod(listPeriod === p.key ? null : p.key)}
                >
                  <span className="sm-period-card__label">{p.label}</span>
                  <span className="sm-period-card__count">{p.count}</span>
                  <span className="sm-period-card__date">{p.date}</span>
                </button>
              ))}
            </div>

          <div className="orders-table card">
            <h2>Order summary by type</h2>
            <div className="table-container">
              <table className="table store-manager-summary-table">
                <thead>
                  <tr>
                    <th>Order type</th>
                    <th className="col-center">Today</th>
                    <th className="col-center">Tomorrow</th>
                    <th className="col-center">Future</th>
                  </tr>
                </thead>
                <tbody>
                  {categories.map(cat => (
                    <tr key={cat}>
                      <td><strong>{formatCategoryLabel(cat)}</strong></td>
                      <td className="col-center">{counts[cat] ? counts[cat].today : 0}</td>
                      <td className="col-center">{counts[cat] ? counts[cat].tomorrow : 0}</td>
                      <td className="col-center">{counts[cat] ? counts[cat].future : 0}</td>
                    </tr>
                  ))}
                  <tr className="click-here-row">
                    <td></td>
                    <td className="col-center">
                      <button type="button" className="btn-link" onClick={() => setListPeriod(listPeriod === 'today' ? null : 'today')}>
                        Open
                      </button>
                    </td>
                    <td className="col-center">
                      <button type="button" className="btn-link" onClick={() => setListPeriod(listPeriod === 'tomorrow' ? null : 'tomorrow')}>
                        Open
                      </button>
                    </td>
                    <td className="col-center">
                      <button type="button" className="btn-link" onClick={() => setListPeriod(listPeriod === 'future' ? null : 'future')}>
                        Open
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {listPeriod && (
              <div className="orders-list-section">
                <h3>Orders — {listLabel}</h3>
                <div className="table-container sm-orders-desktop">
                  <table className="table orders-list-table">
                    <thead>
                      <tr>
                        <th>Order #</th>
                        <th>Customer</th>
                        <th>Category</th>
                        <th>Products</th>
                        <th>Delivery</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {listOrders.length === 0 ? (
                        <tr><td colSpan={6} className="text-muted">No orders</td></tr>
                      ) : (
                        listOrders.map((o) => (
                          <tr key={o.id} className="clickable-row" onClick={() => navigate(`/orders/${o.id}`)}>
                            <td><strong>{o.order_number || `CX-${o.id}`}</strong></td>
                            <td>{o.customer_name}</td>
                            <td>{formatCategoryLabel(o.category)}</td>
                            <td className="product-desc-cell" title={getProductDescription(o)}>{getProductDescription(o)}</td>
                            <td>{formatDateDisplay(o.delivery_date)}{o.delivery_time ? ` ${o.delivery_time}` : ''}</td>
                            <td><span className={`status-badge status-${(o.status || '').toLowerCase().replace(' ', '-')}`}>{o.status || 'Pending'}</span></td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
                <div className="sm-orders-mobile">
                  {listOrders.length === 0 ? (
                    <p className="text-muted">No orders</p>
                  ) : (
                    listOrders.map((o) => (
                      <button
                        key={o.id}
                        type="button"
                        className="sm-order-card"
                        onClick={() => navigate(`/orders/${o.id}`)}
                      >
                        <div className="sm-order-card__top">
                          <strong>{o.order_number || `CX-${o.id}`}</strong>
                          <span className={`status-badge status-${(o.status || '').toLowerCase().replace(' ', '-')}`}>{o.status || 'Pending'}</span>
                        </div>
                        <div className="sm-order-card__name">{o.customer_name}</div>
                        <div className="sm-order-card__meta">{formatCategoryLabel(o.category)} · {formatDateDisplay(o.delivery_date)}{o.delivery_time ? ` ${o.delivery_time}` : ''}</div>
                        <div className="sm-order-card__products">{getProductDescription(o)}</div>
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
          </>
        )}
      </div>
    </div>
  );
};

export default StoreManagerDashboard;
